import React, { useMemo, useState } from 'react';
import { Search, UserPlus, Users, X, ArrowLeft } from 'lucide-react';
import { EmptyState } from '../../components/ui/EmptyState';
import type { PosCustomer } from './types';
import { formatUzs } from './saleTotals';
import { matches } from '../../lib/translit';

interface Props {
  customers: PosCustomer[];
  selected: PosCustomer | null;
  onSelect: (customer: PosCustomer) => void;
  /** `customers.create` ruxsati — yo'q bo'lsa faqat mavjudini tanlash mumkin. */
  canCreate: boolean;
  onBack: () => void;
}

/**
 * Mijoz tanlash yoki joyida yangisini yaratish.
 *
 * Yangi mijoz DARHOL serverga yuborilmaydi: u sotuv bilan bitta so'rovda
 * yaratiladi. Aks holda sotuv oxirigacha yetmasa, bazada mijoz qolib ketardi.
 * Shuning uchun bunday mijozning `id` si `null`.
 */
export const CustomerPicker: React.FC<Props> = ({
  customers,
  selected,
  onSelect,
  canCreate,
  onBack,
}) => {
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [drafts, setDrafts] = useState<PosCustomer[]>([]);

  const all = useMemo(() => [...drafts, ...customers], [drafts, customers]);

  const filtered = useMemo(() => {
    const query = search.trim();
    if (!query) return all;
    // Ism alifboga bog'liq emas, telefon esa raqam bo'yicha.
    return all.filter((c) => matches(c.name, query) || c.phone.includes(query));
  }, [all, search]);

  const canSubmitDraft = name.trim().length > 0 && phone.trim().length > 0;

  const addDraft = () => {
    if (!canSubmitDraft) return;
    const draft: PosCustomer = { id: null, name: name.trim(), phone: phone.trim(), debt: 0 };
    setDrafts((prev) => [draft, ...prev]);
    setAdding(false);
    setName('');
    setPhone('');
    onSelect(draft);
  };

  return (
    <div className="flex flex-col min-h-0 min-w-0">
      <div className="page-bar flex items-center gap-2 mb-1">
        <button
          type="button"
          onClick={onBack}
          className="w-10 h-10 shrink-0 grid place-items-center rounded-lg text-gray-500 hover:bg-gray-100"
          aria-label="Orqaga"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-10"
            placeholder="Ism yoki telefon"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="btn btn-secondary shrink-0 flex items-center gap-2"
          >
            {adding ? <X size={16} /> : <UserPlus size={16} />}
            <span className="hidden sm:inline">{adding ? 'Bekor' : 'Yangi'}</span>
          </button>
        )}
      </div>

      {adding && (
        <div className="card p-4 mb-3 space-y-3">
          <div>
            <label className="label" htmlFor="pos-customer-name">Ism</label>
            <input
              id="pos-customer-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="label" htmlFor="pos-customer-phone">Telefon</label>
            <input
              id="pos-customer-phone"
              className="input"
              type="tel"
              placeholder="+998901234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {/* Mijozlar telefon bo'yicha ajratiladi — telefonsiz mijozga qarz
                biriktirib bo'lmaydi. */}
            {!phone.trim() && (
              <p className="text-xs text-gray-500 mt-1">Telefon majburiy — qarz shu orqali biriktiriladi.</p>
            )}
          </div>
          <button
            type="button"
            className="btn btn-primary w-full"
            disabled={!canSubmitDraft}
            onClick={addDraft}
          >
            Qo'shish
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Mijoz topilmadi"
          description={canCreate ? 'Yangi mijozni shu yerda qo\'shishingiz mumkin.' : undefined}
        />
      ) : (
        <div className="space-y-2 overflow-y-auto min-h-0">
          {filtered.map((customer, index) => {
            const isSelected =
              selected != null &&
              (customer.id != null ? selected.id === customer.id : selected.phone === customer.phone);

            return (
              <button
                key={customer.id ?? `draft-${index}`}
                type="button"
                onClick={() => onSelect(customer)}
                className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                  isSelected
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-100 bg-white hover:bg-gray-50'
                }`}
              >
                <span className="w-9 h-9 shrink-0 rounded-full bg-gray-100 text-gray-600 grid place-items-center font-semibold">
                  {customer.name.charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium text-gray-900 truncate">{customer.name}</span>
                  <span className="block text-sm text-gray-500">{customer.phone}</span>
                </span>
                {customer.id === null && <span className="pill pill-info shrink-0">Yangi</span>}
                {Number(customer.debt) > 0 && (
                  <span className="pill pill-danger shrink-0 tabular-nums">
                    {formatUzs(customer.debt)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
